import HighlightedText from "@/components/controls/HighlightedText";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { personStatusColors } from "@/components/layouts/People/PeopleTable";
import { companyStatusColors } from "@/pages/companies/Companies";
import { leadStatusColors } from "@/pages/leads/helpers";
import { StatusBadge } from "@/components/ui/status-badge";
import { useSearchHighlight } from "@/contexts/SearchHighlightContext";
import { CompanyStatus } from "@/pages/companies/types";
import { DealColumn, DealStatus } from "@/pages/deals/types";
import { LeadStatus } from "@/pages/leads/types";
import { PersonStatus } from "@/entities/people/types";
import { ContractTableStatus } from "@/pages/contracts/types";
import { contractTableStatusColors } from "@/pages/contracts/helpers";
import { PartnerStatus } from "@/pages/partners/types";
import { partnerStatusColors } from "@/pages/partners/helpers";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import { useTranslation } from "react-i18next";

const statusConfig: Partial<
  Record<
    EntityType,
    { namespace: string; subKey: string; enumType: any; colorMap: any }
  >
> = {
  [EntityType.DEAL]: {
    namespace: "stakeholders.dealsModal.table",
    subKey: DealColumn.STATUS,
    enumType: DealStatus,
    colorMap: dealStatusColors
  },
  [EntityType.COMPANY]: {
    namespace: "companies",
    subKey: "statusValues",
    enumType: CompanyStatus,
    colorMap: companyStatusColors
  },
  [EntityType.LEAD]: {
    namespace: "leads",
    subKey: "statusValues",
    enumType: LeadStatus,
    colorMap: leadStatusColors
  },
  [EntityType.PERSON]: {
    namespace: "people",
    subKey: "statuses",
    enumType: PersonStatus,
    colorMap: personStatusColors
  },
  [EntityType.CONTRACT]: {
    namespace: "contracts.table",
    subKey: "statusValues",
    enumType: ContractTableStatus,
    colorMap: contractTableStatusColors
  },
  // Partners and partner contacts share the PartnerStatus enum but keep
  // separate translation namespaces
  [EntityType.PARTNER]: {
    namespace: "partners",
    subKey: "statusValues",
    enumType: PartnerStatus,
    colorMap: partnerStatusColors
  },
  [EntityType.PARTNER_CONTACT]: {
    namespace: "partnercontacts",
    subKey: "statusValues",
    enumType: PartnerStatus,
    colorMap: partnerStatusColors
  }
};

interface Props {
  entityType: EntityType;
  value: string;
}

const EntityStatus: React.FC<Props> = ({ entityType, value }) => {
  const { t } = useTranslation();
  const { searchQuery } = useSearchHighlight();

  const config = statusConfig[entityType];
  if (!config) return null;

  const statusKey = Number(value);
  const valueKey = getEnumTKey(
    config.namespace,
    config.subKey,
    config.enumType,
    statusKey
  );
  return (
    <StatusBadge
      statusColor={config.colorMap[statusKey]}
      containerClassName="w-fit"
    >
      <HighlightedText text={t(valueKey)} query={searchQuery} />
    </StatusBadge>
  );
};

export default EntityStatus;
